import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, Platform, Linking, ActivityIndicator, ScrollView } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { getVisiblePlanList, formatCentsAsDollars, PLAN_IDS, SUBSCRIPTION_PLANS } from '../constants/subscriptionPlans';
import { REFERRAL_DISCOUNT_CENTS } from '../constants/referrals';
import { useTheme } from '../contexts/ThemeContext';
import { useYearlyPricing } from '../contexts/FeatureFlagsContext';
import { getDeployTarget } from '../config/environment';
import { getUserSubscriptionPlanId } from '../utils/subscriptionEntitlements';
import { useUser } from '../components/LoginPersistence';
import { fetchReferralPreview } from '../utils/referralService';

function readReferralCode(route) {
  const fromParams = route?.params?.ref;
  if (fromParams) return String(fromParams).trim();
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return String(new URLSearchParams(window.location.search).get('ref') || '').trim();
  }
  return '';
}

export function Plans({ navigation }) {
  const route = useRoute();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [error, setError] = useState('');
  const { user, setUser } = useUser();
  // Map member → Calendar + Map: first tap asks, second tap upgrades.
  const [confirmUpgrade, setConfirmUpgrade] = useState(false);
  const [notice, setNotice] = useState('');
  // Derived on render so an active subscriber never sees a Subscribe button flash first.
  const userSubscriptionPlanId = user ? getUserSubscriptionPlanId(user) : null;
  const { isDarkMode } = useTheme();
  const yearlyPricing = useYearlyPricing();
  const planList = useMemo(() => getVisiblePlanList({ yearlyPricing }), [yearlyPricing]);
  const referralCode = useMemo(() => readReferralCode(route), [route?.params?.ref]);
  const [referralPreview, setReferralPreview] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!referralCode) {
      setReferralPreview(null);
      return undefined;
    }
    setReferralLoading(true);
    fetchReferralPreview(referralCode)
      .then((data) => {
        if (!cancelled) setReferralPreview(data);
      })
      .catch(() => {
        if (!cancelled) setReferralPreview({ valid: false });
      })
      .finally(() => {
        if (!cancelled) setReferralLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const handleSubscribe = async (planId) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigation.navigate('Login');
      return;
    }

    setError('');
    setLoadingPlanId(planId);
    try {
      const deployTarget = getDeployTarget();
      const origin =
        Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null;
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        planId,
        deployTarget,
        ...(referralPreview?.valid && referralCode
          ? { referralCode: referralPreview.code || referralCode }
          : {}),
        ...(origin
          ? {
              successUrl: `${origin}/subscription-success`,
              cancelUrl: `${origin}/plans${referralCode ? `?ref=${encodeURIComponent(referralCode)}` : ''}`,
            }
          : {}),
      });
      const url = result?.data?.url;
      if (!url) {
        throw new Error('Checkout URL was not returned. Check Stripe price configuration.');
      }
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err?.message || 'Could not start checkout. Please try again.');
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleUpgrade = async (planId) => {
    setError('');
    setLoadingPlanId(planId);
    try {
      const upgrade = httpsCallable(getFunctions(), 'upgradeSubscriptionPlan');
      const result = await upgrade({ planId, deployTarget: getDeployTarget() });
      // The user doc isn't live here; patch it so the cards and Calendar access update now.
      setUser((prev) => (prev ? { ...prev, subscriptionPlan: result?.data?.planId || planId } : prev));
      setConfirmUpgrade(false);
      setNotice(`You're on ${SUBSCRIPTION_PLANS[planId].name}. The Calendar is unlocked; your next bill is ${SUBSCRIPTION_PLANS[planId].priceLabel}${SUBSCRIPTION_PLANS[planId].periodLabel}.`);
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err?.message || 'Could not upgrade. Please try again.');
    } finally {
      setLoadingPlanId(null);
    }
  };

  const referralActive = referralPreview?.valid === true;

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <ScrollView contentContainerStyle={[globalStyles.overlay, globalStyles.plansContent]}>
        <View style={globalStyles.plansHeader}>
          <Text style={[globalStyles.plansTitle, isDarkMode && { color: '#ffffff' }]}>VRC Membership</Text>
          <Text style={[globalStyles.plansSubtitle, isDarkMode && { color: '#ffffff' }]}>
            The tour map on its own, or Map and Calendar together
          </Text>
        </View>

        {referralCode ? (
          <View
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: referralActive ? 'rgba(46,204,113,0.5)' : 'rgba(231,76,60,0.45)',
              maxWidth: 640,
            }}
          >
            {referralLoading ? (
              <ActivityIndicator color="#4e7de0" />
            ) : referralActive ? (
              <>
                <Text style={{ color: isDarkMode ? '#fff' : '#111', fontWeight: '600', marginBottom: 6 }}>
                  Referral applied — {formatCentsAsDollars(REFERRAL_DISCOUNT_CENTS)} off your first payment
                </Text>
                <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.75)' : '#444', fontSize: 14 }}>
                  Applies to the first payment on any plan (code {referralPreview.code})
                </Text>
              </>
            ) : (
              <Text style={{ color: '#ffb4b4' }}>
                Referral code “{referralCode}” is not active. Subscribe at full price or ask for a new link.
              </Text>
            )}
          </View>
        ) : null}

        {notice ? (
          <Text style={[globalStyles.plansSubtitle, { color: '#9be7b4', marginBottom: 16, maxWidth: 640 }]}>
            {notice}
          </Text>
        ) : null}

        {error ? (
          <Text style={[globalStyles.plansSubtitle, { color: '#ffb4b4', marginBottom: 16, maxWidth: 640 }]}>
            {error}
          </Text>
        ) : null}

        <View style={globalStyles.plansRow}>
          {planList.map((plan) => {
            const busy = loadingPlanId === plan.id;
            const discountCents =
              referralActive ? referralPreview.discountCents ?? REFERRAL_DISCOUNT_CENTS : 0;
            const displayCents = Math.max(0, plan.priceCents - discountCents);
            const firstLabel = plan.yearly ? ' first year' : ' first month';
            const shortPeriod = plan.yearly ? '/yr' : '/mo';
            // Any active subscriber manages their plan instead of starting a second checkout.
            const isSubscribed = !!userSubscriptionPlanId;
            const isCurrent = userSubscriptionPlanId === plan.id;
            // A cancelled Map member resumes first (Manage Subscription); the server refuses it anyway.
            const canUpgrade =
              userSubscriptionPlanId === PLAN_IDS.MAP_ONLY &&
              plan.id === PLAN_IDS.VRC_MEMBER &&
              user?.subscriptionStatus !== 'cancelled';
            return (
              <View
                key={plan.id}
                style={[globalStyles.planCard, globalStyles.planCardHighlighted]}
              >
                {isCurrent && (
                  <View style={globalStyles.currentPlanBadge}>✓ Current Plan</View>
                )}
                <View style={globalStyles.planHeader}>
                  <Text style={globalStyles.planName}>{plan.name}</Text>
                  <View style={globalStyles.priceContainer}>
                    <Text style={globalStyles.priceCurrency}>$</Text>
                    <Text style={globalStyles.priceAmount}>{Math.round(displayCents / 100)}</Text>
                    <Text style={globalStyles.pricePeriod}>
                      {referralActive ? firstLabel : plan.periodLabel}
                    </Text>
                  </View>
                  {referralActive ? (
                    <Text style={globalStyles.planDiscountHint}>
                      Then {plan.priceLabel}{shortPeriod} · referral −{formatCentsAsDollars(REFERRAL_DISCOUNT_CENTS)}
                    </Text>
                  ) : (
                    <Text style={globalStyles.planDiscountHint}>
                      Earn Credits from approved map contributions
                    </Text>
                  )}
                </View>

                <View style={globalStyles.featuresContainer}>
                  <Text style={globalStyles.featuresTitle}>What's Included:</Text>
                  {plan.features.map((feature) => (
                    <View key={feature} style={globalStyles.featureItem}>
                      <Text style={globalStyles.featureIcon}>✓</Text>
                      <Text style={globalStyles.featureText}>{feature}</Text>
                    </View>
                  ))}
                </View>

                <Text style={[globalStyles.featureText, { opacity: 0.85, marginBottom: 16, textAlign: 'center' }]}>
                  {plan.blurb}
                </Text>

                {canUpgrade ? (
                  <>
                    {confirmUpgrade ? (
                      <Text style={[globalStyles.featureText, { marginBottom: 10, textAlign: 'center' }]}>
                        The Calendar unlocks now and nothing is charged today. From your next bill you pay {plan.priceLabel}{plan.periodLabel} instead of {SUBSCRIPTION_PLANS[PLAN_IDS.MAP_ONLY].priceLabel}.
                      </Text>
                    ) : null}
                    <TouchableOpacity
                      style={[globalStyles.subscribeButton, busy && { opacity: 0.7 }]}
                      onPress={() => (confirmUpgrade ? handleUpgrade(plan.id) : setConfirmUpgrade(true))}
                      disabled={!!loadingPlanId}
                      accessibilityRole="button"
                    >
                      {busy ? (
                        <ActivityIndicator color="#4e7de0" />
                      ) : (
                        <Text style={globalStyles.subscribeButtonText}>
                          {confirmUpgrade ? `Confirm upgrade — ${plan.priceLabel}${shortPeriod}` : `Upgrade to ${plan.name}`}
                        </Text>
                      )}
                    </TouchableOpacity>
                    {confirmUpgrade && !busy ? (
                      <TouchableOpacity onPress={() => setConfirmUpgrade(false)} accessibilityRole="button" style={{ alignSelf: 'center', padding: 8 }}>
                        <Text style={globalStyles.guaranteeText}>Not now</Text>
                      </TouchableOpacity>
                    ) : null}
                  </>
                ) : isSubscribed ? (
                  <TouchableOpacity
                    style={[globalStyles.subscribeButton, busy && { opacity: 0.7 }]}
                    onPress={() => navigation.navigate('Wallet', { tab: 'membership' })}
                    disabled={!!loadingPlanId}
                  >
                    {busy ? (
                      <ActivityIndicator color="#4e7de0" />
                    ) : (
                      <Text style={globalStyles.subscribeButtonText}>Manage Subscription</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[globalStyles.subscribeButton, busy && { opacity: 0.7 }]}
                    onPress={() => handleSubscribe(plan.id)}
                    disabled={!!loadingPlanId}
                  >
                    {busy ? (
                      <ActivityIndicator color="#4e7de0" />
                    ) : (
                      <Text style={globalStyles.subscribeButtonText}>
                        Subscribe — {formatCentsAsDollars(displayCents)}
                        {referralActive ? firstLabel : shortPeriod}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}

                <Text style={globalStyles.guaranteeText}>Cancel anytime</Text>
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}
